import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Home
} from 'lucide-react';
import { Link } from 'react-router-dom';

const MaterialPage = () => {
  const materials = [
    {
      name: 'Budget Shutters',
      subtitle: 'Best Value',
      description: 'Quality shutters at an affordable price point. Perfect for homeowners who want beautiful shutters without breaking the bank. Includes survey, professional installation, and VAT.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Excellent value for money',
        '10 Year Structural Warranty',
        'Survey included',
        'Professional installation included',
        'VAT included',
        'Quality construction',
        'Range of colours available'
      ],
      bestFor: ['First-time buyers', 'Rental properties', 'Budget-conscious homeowners', 'Multi-room projects'],
      warranty: '10 Year Structural Warranty',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      link: '/ranges/budget'
    },
    {
      name: 'Polymer Shutters',
      subtitle: 'Waterproof Range',
      description: 'Market leading water resistance with full hidden tilt option. Made from certified non-toxic virgin material, flame retardant and EU REACH compliant.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/128a6a6620e3f91b5c428df7e5edbce10d4f071a1b206bcb32d695f1a1f3a456.png',
      features: [
        'Certified Non Toxic',
        'EU REACH Compliant',
        'Flame Retardant',
        '10 Year Structural Warranty',
        'Made From Virgin Material, Not Recycled',
        'Suitable for Commercial Environments',
        'Maintenance Free & Easy Cleaning',
        'Full hidden tilt option available'
      ],
      bestFor: ['Bathrooms', 'Kitchens', 'Commercial properties', 'Utility rooms'],
      warranty: '10 Year Structural Warranty',
      color: 'text-[#8CB89F]',
      bgColor: 'bg-[#8CB89F]/10',
      link: '/ranges/polymer'
    },
    {
      name: 'Paulownia+ Shutters',
      subtitle: 'Recommended',
      description: 'Lightweight hardwood shutters encased in a hard wearing polymer casing. The perfect combination of natural beauty, durability and value.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Hardwood encased in polymer casing',
        'Hard wearing & durable',
        'Lightweight yet strong',
        'Excellent thermal insulation',
        '10 Year Structural Warranty',
        'Available in over 30 colours',
        '6 natural wood stains available',
        'Custom colours available'
      ],
      bestFor: ['Living rooms', 'Bedrooms', 'Dining rooms', 'Study areas', 'Home offices'],
      warranty: '10 Year Structural Warranty',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/ranges/paulownia',
      recommended: true
    },
    {
      name: 'Basswood Shutters',
      subtitle: 'Premium Range',
      description: 'FSC certified finest Basswood hardwood timber shutters. Smooth grain with harder impact resistance and a smoother finish for the most discerning homeowners.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg',
      features: [
        'FSC certified Basswood hardwood',
        'Smooth grain finish',
        'Harder impact resistance',
        'Superior quality construction',
        'Smoother premium finish',
        '10 Year Structural Warranty',
        'Available in over 30 colours and 6 stains',
        'Custom colours available'
      ],
      bestFor: ['Master bedrooms', 'Formal dining rooms', 'Executive offices', 'Luxury living spaces'],
      warranty: '10 Year Structural Warranty',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      link: '/ranges/basswood'
    },
    {
      name: 'UK Made Basswood Shutters',
      subtitle: 'Premium UK Manufactured',
      description: 'Premium basswood shutters manufactured right here in the UK. Exceptional craftsmanship with a shorter 4-5 week lead time for those who want the very best.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg',
      features: [
        'Made in the UK',
        '4-5 week lead time',
        'Premium quality basswood',
        'Superior finish & craftsmanship',
        '10 Year Structural Warranty',
        'Wide colour range available',
        'Custom colours available'
      ],
      bestFor: ['Luxury homes', 'Heritage properties', 'Bespoke projects', 'Time-sensitive installations'],
      warranty: '10 Year Structural Warranty',
      color: 'text-sky-600',
      bgColor: 'bg-sky-50',
      link: '/ranges/uk-basswood'
    },
    {
      name: 'Aluminium Shutters',
      subtitle: 'Security & External Range',
      description: 'Built from T6 Aircraft Grade Aluminium, designed for external use and to withstand harsh UK weather conditions. Perfect for added home security with lock and key options.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'For External Use',
        'Built To Withstand Harsh UK Weather Conditions',
        'Internal Installation With Locks & Keys For Security',
        'T6 Aircraft Grade Aluminium',
        'Vertical or Horizontal Louvres',
        'Powder Coated Finish',
        'Low Maintenance'
      ],
      bestFor: ['External windows', 'Security applications', 'Commercial premises', 'Coastal properties'],
      warranty: '10 Year Structural Warranty',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      link: '/ranges/aluminium'
    }
  ];

  const comparisonFeatures = [
    { feature: 'Durability', budget: '★★★☆☆', polymer: '★★★★★', paulownia: '★★★★☆', basswood: '★★★★★', uk_basswood: '★★★★★', aluminium: '★★★★★' },
    { feature: 'Moisture Resistance', budget: '★★★☆☆', polymer: '★★★★★', paulownia: '★★★★☆', basswood: '★★★☆☆', uk_basswood: '★★★☆☆', aluminium: '★★★★★' },
    { feature: 'Natural Beauty', budget: '★★★☆☆', polymer: '★★★☆☆', paulownia: '★★★★☆', basswood: '★★★★★', uk_basswood: '★★★★★', aluminium: '★★★☆☆' },
    { feature: 'Premium Feel', budget: '★★★☆☆', polymer: '★★★☆☆', paulownia: '★★★★☆', basswood: '★★★★★', uk_basswood: '★★★★★', aluminium: '★★★★☆' },
    { feature: 'Easy Maintenance', budget: '★★★☆☆', polymer: '★★★★★', paulownia: '★★★★★', basswood: '★★★☆☆', uk_basswood: '★★★☆☆', aluminium: '★★★★★' },
    { feature: 'Security', budget: '★★★☆☆', polymer: '★★★☆☆', paulownia: '★★★☆☆', basswood: '★★★☆☆', uk_basswood: '★★★☆☆', aluminium: '★★★★★' },
    { feature: 'Lead Time', budget: '★★★★☆', polymer: '★★★★☆', paulownia: '★★★★☆', basswood: '★★★★☆', uk_basswood: '★★★★★', aluminium: '★★★☆☆' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gradient-to-br from-slate-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Our Shutter Ranges
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Choose from six carefully selected material ranges to suit every home and budget.
              All shutters include a free home survey, professional installation, and a comprehensive warranty.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-20">
            {materials.map((material, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img src={material.image} alt={material.name} className="w-full h-96 object-cover rounded-2xl shadow-xl" />
                </div>
                <div className="lg:w-1/2">
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${material.bgColor} mb-4`}>
                    <Badge variant="secondary" className={material.color}>{material.subtitle}</Badge>
                    {material.recommended && <Badge className="bg-amber-500 text-white ml-1">Most Popular</Badge>}
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">{material.name}</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{material.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {material.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5" style={{color: '#8CB89F'}} />
                    <span className="font-semibold">{material.warranty}</span>
                  </div>
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Best for:</h4>
                    <div className="flex flex-wrap gap-2">
                      {material.bestFor.map((room, i) => (<Badge key={i} variant="outline">{room}</Badge>))}
                    </div>
                  </div>
                  <Link to={material.link}>
                    <Button className="bg-[#2F4F4F] hover:bg-[#3d6363] text-white">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Material Comparison</h2>
            <p className="text-xl text-gray-600">Compare key features to find the perfect material for your needs</p>
          </div>
          <Card className="overflow-hidden shadow-xl">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold text-emerald-600">Budget</th>
                      <th className="text-center p-4 font-semibold text-[#8CB89F]">Polymer</th>
                      <th className="text-center p-4 font-semibold text-green-600">Paulownia+</th>
                      <th className="text-center p-4 font-semibold text-amber-600">Basswood</th>
                      <th className="text-center p-4 font-semibold text-sky-600">UK Basswood</th>
                      <th className="text-center p-4 font-semibold text-gray-600">Aluminium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-4 font-medium">{row.feature}</td>
                        <td className="p-4 text-center text-xl">{row.budget}</td>
                        <td className="p-4 text-center text-xl">{row.polymer}</td>
                        <td className="p-4 text-center text-xl">{row.paulownia}</td>
                        <td className="p-4 text-center text-xl">{row.basswood}</td>
                        <td className="p-4 text-center text-xl">{row.uk_basswood}</td>
                        <td className="p-4 text-center text-xl">{row.aluminium}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Choose Your Material?</h2>
          <p className="text-xl mb-8 opacity-90">
            Use our online estimator to get an instant quote, or book a free home survey with one of our experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg">
                <Home className="mr-2 h-5 w-5" />
                Book Free Survey
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MaterialPage;
